import { useState } from "react";
import { Link } from "react-router-dom";
import { TopBar } from "../../components/TopBar";
import { Modal } from "../../components/Modal";
import { PageHeader } from "../../components/PageHeader";
import { StatStrip } from "../../components/StatStrip";
import { DataTable } from "../../components/DataTable";
import { Badge } from "../../components/Badge";
import { Segmented } from "../../components/Controls";
import { useProjects } from "../../context/ProjectContext";
import { useAuth } from "../../context/AuthContext";
import { useAsync } from "../../lib/useAsync";
import { useToast } from "../../context/ToastContext";
import { api } from "../../api/client";
import { timeAgo, formatBytes } from "../../lib/format";
import type { PlanKey } from "../../api/types";

const PLAN_LABELS: Record<PlanKey, string> = { free: "Free", indie: "Indie", pro: "Pro" };

export function Overview() {
  const { active } = useProjects();
  const { user } = useAuth();
  const pid = active?.id ?? "";
  const toast = useToast();

  const boards = useAsync(() => api.listBoards(pid), [pid]);
  const keys = useAsync(() => api.listKeys(pid), [pid]);

  const activeAccountId = api.getActiveAccountId();
  const usage = useAsync(
    () => (activeAccountId ? api.getUsage(activeAccountId) : Promise.resolve(null)),
    [activeAccountId ?? ""],
  );

  const totalEntries = (boards.data ?? []).reduce((s, b) => s + b.size, 0);
  const activeKeys = (keys.data ?? []).filter((k) => !k.revoked_at).length;
  const currentPlan: PlanKey = usage.data?.plan.key ?? "indie";

  const [newAccountOpen, setNewAccountOpen] = useState(false);
  const [newAccountName, setNewAccountName] = useState("");
  const [newAccountBusy, setNewAccountBusy] = useState(false);
  const [planBusy, setPlanBusy] = useState(false);

  async function handleSetPlan(key: PlanKey) {
    if (!activeAccountId) return;
    setPlanBusy(true);
    try {
      await api.setPlan(activeAccountId, key);
      toast(`Plan changed to ${PLAN_LABELS[key]}`);
      usage.reload?.();
    } catch (e) {
      toast((e as Error).message, "err");
    } finally {
      setPlanBusy(false);
    }
  }

  async function handleCreateAccount(e: React.FormEvent) {
    e.preventDefault();
    setNewAccountBusy(true);
    try {
      const acc = await api.createAccount(newAccountName.trim());
      toast(`Account "${acc.name}" created`);
      setNewAccountOpen(false);
      setNewAccountName("");
    } catch (ex) {
      toast((ex as Error).message, "err");
    } finally {
      setNewAccountBusy(false);
    }
  }

  return (
    <>
      <TopBar crumbs={<b>Overview</b>} />
      <div className="content">
        <PageHeader
          title={`Welcome back, ${user?.name?.split(" ")[0] ?? "there"}.`}
          intent={<>Here's how <b style={{ color: "var(--fg)" }}>{active?.name}</b> is climbing.</>}
          actions={<Link to="/app/boards/new" className="btn btn-primary">＋ New leaderboard</Link>}
        />

        <StatStrip
          stats={[
            { label: "Leaderboards", value: boards.loading ? "—" : boards.data?.length ?? 0 },
            { label: "Total entries", value: boards.loading ? "—" : totalEntries.toLocaleString() },
            { label: "Active API keys", value: keys.loading ? "—" : activeKeys },
            { label: "Plan", value: usage.loading ? "—" : PLAN_LABELS[currentPlan] },
          ]}
        />

        {/* Plan switcher + usage */}
        <div className="card" style={{ marginTop: 24, padding: "16px 20px" }}>
          <div className="between" style={{ marginBottom: 14 }}>
            <h3 style={{ fontSize: 16 }}>Plan &amp; usage</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => setNewAccountOpen(true)}>＋ New account</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Segmented<PlanKey>
              value={currentPlan}
              onChange={handleSetPlan}
              disabled={planBusy || usage.loading}
              options={[
                { value: "free", label: "Free" },
                { value: "indie", label: "Indie" },
                { value: "pro", label: "Pro" },
              ]}
            />
            {usage.data && (
              <div className="row" style={{ gap: 24, flexWrap: "wrap", marginTop: 4 }}>
                <span className="muted" style={{ fontSize: 13 }}>
                  Boards: <b style={{ color: "var(--fg)" }}>{usage.data.usage.board_count}</b>
                  {usage.data.plan.max_boards != null && <> / {usage.data.plan.max_boards}</>}
                </span>
                <span className="muted" style={{ fontSize: 13 }}>
                  Writes this month: <b style={{ color: "var(--fg)" }}>{usage.data.usage.writes.toLocaleString()}</b>
                  {usage.data.plan.monthly_writes != null && <> / {usage.data.plan.monthly_writes.toLocaleString()}</>}
                </span>
                {usage.data.usage.blob_bytes > 0 && (
                  <span className="muted" style={{ fontSize: 13 }}>
                    Replay storage: <b style={{ color: "var(--fg)" }}>{formatBytes(usage.data.usage.blob_bytes)}</b>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="card" style={{ marginTop: 24, padding: "6px 0" }}>
          <div className="between" style={{ padding: "14px 20px" }}>
            <h3 style={{ fontSize: 18 }}>Your leaderboards</h3>
            <Link to="/app/boards" className="muted" style={{ fontSize: 13.5 }}>View all →</Link>
          </div>
          {boards.loading ? (
            <div className="loading-rows">
              {[0, 1, 2].map((i) => <div key={i} className="skel" />)}
            </div>
          ) : (
            <DataTable
              rows={boards.data ?? []}
              rowKey={(b) => b.id}
              empty={
                <p className="muted" style={{ padding: "8px 20px 22px" }}>
                  No boards yet. <Link to="/app/boards/new" style={{ color: "var(--mint)" }}>Create your first →</Link>
                </p>
              }
              columns={[
                {
                  header: "Board",
                  cell: (b) => (
                    <Link to={`/app/boards/${encodeURIComponent(b.id)}`} style={{ color: "var(--mint)" }}>
                      {b.name || b.id}
                    </Link>
                  ),
                },
                {
                  header: "Ranking",
                  cell: (b) => <Badge tone="dim">{b.sort_order === "asc" ? "Lowest wins" : "Highest wins"}</Badge>,
                },
                { header: "Entries", cell: (b) => b.size.toLocaleString(), className: "num", right: true },
                {
                  header: "Recent (24h)",
                  cell: (b) => (b.recent_updates != null ? `${b.recent_updates}` : "—"),
                  className: "num muted",
                  right: true,
                },
                {
                  header: "Last activity",
                  cell: (b) => <span className="faint">{timeAgo(b.last_activity_at ?? b.updated_at)}</span>,
                },
              ]}
            />
          )}
        </div>
      </div>

      {newAccountOpen && (
        <Modal title="New account" onClose={() => setNewAccountOpen(false)}>
          <form className="modal-body" onSubmit={handleCreateAccount}>
            <div className="field">
              <label htmlFor="acc-name">Account name</label>
              <input
                id="acc-name"
                className="input"
                autoFocus
                value={newAccountName}
                onChange={(e) => setNewAccountName(e.target.value)}
                placeholder="My Studio"
                required
              />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setNewAccountOpen(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={newAccountBusy || !newAccountName.trim()}>
                {newAccountBusy ? <span className="spinner" /> : "Create account"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
